import { gql } from 'graphql-request';
import { getGraphQLQueryClient } from './query-client';
import {
  ListingStatus,
  Nullable,
  UserWithToken,
  WithData,
} from '@ygoargentina/sdk/api/api.types';

// =============================================================================
// TYPES
// =============================================================================

export interface AccessoryListing {
  id: number;
  name: string;
  description?: string;
  imageUrl?: string;
  productId?: number;
  quantity: number;
  priceType: string;
  salePrice: number;
  salePriceUSD?: number;
  customDollarRate?: number;
  isNegotiable: boolean;
  condition?: string;
  status: ListingStatus;
  product?: {
    id: number;
    name: string;
    description?: string;
    brand?: string;
    imageUrl?: string;
    productType: string;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateAccessoryListingInput {
  productId?: number;
  name: string;
  description?: string;
  imageUrl?: string;
  quantity?: number;
  priceType: string;
  salePrice?: number;
  salePriceUSD?: number;
  customDollarRate?: number;
  isNegotiable?: boolean;
  condition?: string;
}

export interface UpdateAccessoryListingInput {
  id: number;
  productId?: number;
  name?: string;
  description?: string;
  imageUrl?: string;
  quantity?: number;
  priceType?: string;
  salePrice?: number;
  salePriceUSD?: number;
  customDollarRate?: number;
  isNegotiable?: boolean;
  condition?: string;
}

// =============================================================================
// FRAGMENTS
// =============================================================================

const ACCESSORY_LISTING_FRAGMENT = gql`
  fragment AccessoryListingFields on AccessoryListing {
    id
    name
    description
    imageUrl
    productId
    quantity
    priceType
    salePrice
    salePriceUSD
    customDollarRate
    isNegotiable
    condition
    status
    product {
      id
      name
      description
      brand
      imageUrl
      productType
    }
    createdAt
    updatedAt
  }
`;

// =============================================================================
// QUERIES
// =============================================================================

/**
 * `AccessoryListingList` pagina con `total`/`page`/`limit` planos, no con el
 * objeto `meta` que usan otras listas del API. Pedía `meta` y eso rechazaba la
 * query ENTERA en validación: la pantalla de mis accesorios no mostraba nada y
 * no había error a la vista (YGO-305).
 */
export type AccessoryListingPage = {
  data: {
    data: AccessoryListing[];
    total: number;
    page: number;
    limit: number;
  };
};

export const getMyAccessoryListings = async (
  user: Nullable<UserWithToken>,
  variables?: { status?: ListingStatus; page?: number; limit?: number }
) =>
  getGraphQLQueryClient(user).request<AccessoryListingPage>(
    gql`
      ${ACCESSORY_LISTING_FRAGMENT}
      query myAccessoryListings($status: ListingStatus, $page: Int, $limit: Int) {
        data: myAccessoryListings(status: $status, page: $page, limit: $limit) {
          data {
            ...AccessoryListingFields
          }
          total
          page
          limit
        }
      }
    `,
    variables
  );

export const getAccessoryListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<AccessoryListing | null>>(
    gql`
      ${ACCESSORY_LISTING_FRAGMENT}
      query accessoryListing($id: Int!) {
        data: accessoryListing(id: $id) {
          ...AccessoryListingFields
          events {
            id
            type
            message
            createdAt
            # El evento guarda el id suelto, no la relación: no hay usuario
            # que expandir acá.
            userId
          }
        }
      }
    `,
    variables
  );

// =============================================================================
// MUTATIONS
// =============================================================================

/**
 * Sube la foto del accesorio y devuelve su URL pública, para mandarla como
 * `imageUrl` al crear o editar. `image` va en base64 (con o sin el prefijo
 * `data:`); el API acepta jpeg, png y webp, hasta 4 MB.
 */
export const uploadAccessoryImage = async (
  user: Nullable<UserWithToken>,
  variables: { image: string; contentType: string }
) =>
  getGraphQLQueryClient(user).request<WithData<string>>(
    gql`
      mutation uploadAccessoryImage($image: String!, $contentType: String!) {
        data: uploadAccessoryImage(image: $image, contentType: $contentType)
      }
    `,
    variables
  );

export const createAccessoryListing = async (
  user: Nullable<UserWithToken>,
  variables: { input: CreateAccessoryListingInput }
) =>
  getGraphQLQueryClient(user).request<WithData<AccessoryListing>>(
    gql`
      ${ACCESSORY_LISTING_FRAGMENT}
      mutation createAccessoryListing($input: CreateAccessoryListingInput!) {
        data: createAccessoryListing(input: $input) {
          ...AccessoryListingFields
        }
      }
    `,
    variables
  );

export const updateAccessoryListing = async (
  user: Nullable<UserWithToken>,
  variables: { input: UpdateAccessoryListingInput }
) =>
  getGraphQLQueryClient(user).request<WithData<AccessoryListing>>(
    gql`
      ${ACCESSORY_LISTING_FRAGMENT}
      mutation updateAccessoryListing($input: UpdateAccessoryListingInput!) {
        data: updateAccessoryListing(input: $input) {
          ...AccessoryListingFields
        }
      }
    `,
    variables
  );

export const pauseAccessoryListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<AccessoryListing>>(
    gql`
      ${ACCESSORY_LISTING_FRAGMENT}
      mutation pauseAccessoryListing($id: Int!) {
        data: pauseAccessoryListing(id: $id) {
          ...AccessoryListingFields
        }
      }
    `,
    variables
  );

export const activateAccessoryListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<AccessoryListing>>(
    gql`
      ${ACCESSORY_LISTING_FRAGMENT}
      mutation activateAccessoryListing($id: Int!) {
        data: activateAccessoryListing(id: $id) {
          ...AccessoryListingFields
        }
      }
    `,
    variables
  );

export const deleteAccessoryListing = async (
  user: Nullable<UserWithToken>,
  variables: { id: number }
) =>
  getGraphQLQueryClient(user).request<WithData<boolean>>(
    gql`
      mutation deleteAccessoryListing($id: Int!) {
        data: deleteAccessoryListing(id: $id)
      }
    `,
    variables
  );
